import React, { useCallback, useMemo, useRef, useState } from 'react';
import { OAE_Y_MIN, OAE_Y_MAX, OAE_PASS_SNR_DB, pointAt, fmtFreq } from './oaeLogic';

/**
 * OAEGraph — interactive SVG line chart for a single ear.
 *
 * • X-axis: Frequency (kHz) — one tick per test frequency
 * • Y-axis: Amplitude (dB SPL) — range OAE_Y_MIN..OAE_Y_MAX (-20..+30)
 * • Line 1: OAE Response (colored, red for right / blue for left)
 * • Line 2: Noise Floor (grey)
 * • Green shaded band above 0 dB — normal OAE response envelope
 *
 * Click-to-plot: user selects the active plotter (OAE Response or
 * Noise Floor) via a segmented toggle above the graph. Clicking
 * inside the plot area finds the nearest frequency column and writes
 * that dB value into the corresponding cell of the table (bubbled
 * via `onPlotPoint(freqHz, kind, dbValue)`).
 *
 * The graph is view-only in `readOnly` mode (used by the report modal
 * + the Reports section on the standard hearing-report layout).
 */
const OAEGraph = ({
  ear,           // 'right' | 'left'
  earData,       // {points: {[freq]: {oae?, noise?}}}
  freqs,         // array of frequencies in Hz
  plotKind,      // 'oae' | 'noise'  (active click target)
  onPlotPoint,   // (freqHz, kind, dbValue) => void
  readOnly = false,
  width = 460,
  height = 260,
  passResult = null, // 'Pass' | 'Refer' | null — for badge
}) => {
  const svgRef = useRef(null);
  const oaeColor = ear === 'right' ? '#dc2626' : '#2563eb';
  const marginL = 42, marginR = 12, marginT = 18, marginB = 34;
  const w = width, h = height;
  const plotW = w - marginL - marginR;
  const plotH = h - marginT - marginB;

  const xScale = useCallback((i) => marginL + (i * plotW) / Math.max(1, freqs.length - 1), [freqs.length, marginL, plotW]);
  const yScale = useCallback(
    (db) => marginT + ((OAE_Y_MAX - db) * plotH) / (OAE_Y_MAX - OAE_Y_MIN),
    [marginT, plotH],
  );
  const yInv = useCallback(
    (py) => {
      const raw = OAE_Y_MAX - ((py - marginT) * (OAE_Y_MAX - OAE_Y_MIN)) / plotH;
      return Math.max(OAE_Y_MIN, Math.min(OAE_Y_MAX, Math.round(raw)));
    },
    [marginT, plotH],
  );

  // Build path strings (skip gaps where data missing)
  const buildPath = useCallback((kind) => {
    let d = '';
    freqs.forEach((f, i) => {
      const p = earData?.points?.[String(f)];
      const v = p?.[kind];
      if (typeof v === 'number') {
        const cmd = d ? 'L' : 'M';
        d += `${cmd}${xScale(i)},${yScale(v)} `;
      } else {
        d += ''; // gap — next M will restart
      }
    });
    return d.trim();
  }, [earData, freqs, xScale, yScale]);

  const oaePath = useMemo(() => buildPath('oae'), [buildPath]);
  const noisePath = useMemo(() => buildPath('noise'), [buildPath]);

  // Ticks
  const yTicks = [-20, -10, 0, 10, 20, 30];
  const passBadgeStyle = passResult === 'Pass'
    ? { bg: '#10b981', color: '#ffffff', label: 'PASS' }
    : passResult === 'Refer'
      ? { bg: '#ef4444', color: '#ffffff', label: 'REFER' }
      : { bg: '#94a3b8', color: '#ffffff', label: 'NO DATA' };

  // Map a browser mouse event → SVG viewBox coordinates using the
  // element's live CTM. This is CORRECT under every preserveAspectRatio
  // mode, DPR change, and container-resize. The previous linear
  // (clientX / rect.width) * w math silently drifted whenever the
  // container was wider than the viewBox aspect (default meet mode
  // letterboxes with mid-align gutters) — the reason clicks at 1 kHz
  // /15 dB were landing OUTSIDE the plot bounds and getting rejected.
  const clientToViewBox = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const ctm = svg.getScreenCTM && svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }, []);

  // Snap the raw viewBox point to the nearest frequency column so the
  // dot always lands on a valid data cell — matches what the audiogram
  // canvas does. Returns null when the click is outside the plot area.
  const snapToNearest = useCallback((pt) => {
    if (!pt) return null;
    const { x, y } = pt;
    // Allow a small tolerance around the axes so edge clicks still fire
    // (marginL/marginR are only 12 & 42 px so a strict check rejects too
    // many real intentional clicks near the outer freq ticks).
    if (y < marginT - 4 || y > (h - marginB) + 4) return null;
    if (x < marginL - 8 || x > (w - marginR) + 8) return null;
    let bestIdx = 0, bestDx = Infinity;
    freqs.forEach((_, i) => {
      const dx = Math.abs(xScale(i) - x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    const clampedY = Math.max(marginT, Math.min(h - marginB, y));
    return { freqIdx: bestIdx, freq: freqs[bestIdx], db: yInv(clampedY) };
  }, [freqs, marginT, marginB, marginL, marginR, plotH, plotW, xScale, yInv, h, w]);

  const [hover, setHover] = useState(null); // {freqIdx, freq, db}

  const handleClick = (evt) => {
    if (readOnly || !onPlotPoint || !plotKind) return;
    const snap = snapToNearest(clientToViewBox(evt));
    if (!snap) return;
    onPlotPoint(snap.freq, plotKind, snap.db);
  };

  const handleMove = (evt) => {
    if (readOnly || !plotKind) return;
    const snap = snapToNearest(clientToViewBox(evt));
    setHover(snap);
  };
  const handleLeave = () => setHover(null);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <div
          data-testid={`oae-graph-${ear}-badge`}
          className="px-2 py-0.5 text-xs font-bold uppercase rounded"
          style={{ backgroundColor: passBadgeStyle.bg, color: passBadgeStyle.color }}
        >
          {ear === 'right' ? 'Right' : 'Left'} · {passBadgeStyle.label}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1 text-[10px]">
            <span className="text-slate-500">Click to plot:</span>
            <button
              data-testid={`oae-plot-mode-${ear}-oae`}
              type="button"
              onClick={() => onPlotPoint?.('__mode__', 'oae', null)}
              className={`px-1.5 py-0.5 rounded font-semibold ${plotKind === 'oae' ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              style={plotKind === 'oae' ? { backgroundColor: oaeColor } : {}}
            >
              OAE
            </button>
            <button
              data-testid={`oae-plot-mode-${ear}-noise`}
              type="button"
              onClick={() => onPlotPoint?.('__mode__', 'noise', null)}
              className={`px-1.5 py-0.5 rounded font-semibold ${plotKind === 'noise' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              Noise
            </button>
          </div>
        )}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        data-testid={`oae-graph-${ear}`}
        style={{ cursor: readOnly ? 'default' : 'crosshair', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block' }}
      >
        {/* Normal OAE band (0..30 dB) */}
        <rect
          x={marginL}
          y={yScale(OAE_Y_MAX)}
          width={plotW}
          height={yScale(0) - yScale(OAE_Y_MAX)}
          fill="#dcfce7"
          opacity="0.55"
        />
        {/* 0 dB reference line */}
        <line x1={marginL} x2={w - marginR} y1={yScale(0)} y2={yScale(0)} stroke="#6ee7b7" strokeWidth="1" strokeDasharray="4 3" />
        {/* Y-axis grid + labels */}
        {yTicks.map((t) => (
          <g key={`y-${t}`}>
            <line x1={marginL} x2={w - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        {/* X-axis labels */}
        {freqs.map((f, i) => (
          <g key={`x-${f}`}>
            <line x1={xScale(i)} x2={xScale(i)} y1={marginT} y2={h - marginB} stroke="#f1f5f9" strokeWidth="0.5" />
            <text x={xScale(i)} y={h - marginB + 14} fontSize="9" fill="#334155" textAnchor="middle" fontWeight="600">
              {fmtFreq(f)}
            </text>
          </g>
        ))}
        {/* Axis labels */}
        <text x={marginL} y={12} fontSize="9" fill="#475569">Amplitude (dB SPL)</text>
        <text x={w - marginR} y={h - 4} fontSize="9" fill="#475569" textAnchor="end">Frequency (kHz)</text>
        {/* Noise floor line */}
        {noisePath && (
          <path d={noisePath} fill="none" stroke="#64748b" strokeWidth="1.5" strokeDasharray="5 3" />
        )}
        {/* Noise floor points */}
        {freqs.map((f, i) => {
          const v = earData?.points?.[String(f)]?.noise;
          if (typeof v !== 'number') return null;
          return <circle key={`np-${f}`} cx={xScale(i)} cy={yScale(v)} r="3" fill="#ffffff" stroke="#64748b" strokeWidth="1.5" />;
        })}
        {/* OAE response line */}
        {oaePath && (
          <path d={oaePath} fill="none" stroke={oaeColor} strokeWidth="2.5" />
        )}
        {/* OAE points */}
        {freqs.map((f, i) => {
          const v = earData?.points?.[String(f)]?.oae;
          if (typeof v !== 'number') return null;
          return <circle key={`op-${f}`} cx={xScale(i)} cy={yScale(v)} r="3.5" fill={oaeColor} />;
        })}
        {/* Per-point pass/refer markers */}
        {freqs.map((f, i) => {
          const { snr } = pointAt(earData, f);
          if (snr === null) return null;
          const pass = snr >= OAE_PASS_SNR_DB;
          return (
            <text key={`r-${f}`} x={xScale(i)} y={marginT - 3} fontSize="8" fontWeight="700"
              fill={pass ? '#059669' : '#dc2626'} textAnchor="middle">
              {pass ? '✓' : '✗'}
            </text>
          );
        })}
        {/* Hover ghost — snaps to nearest freq column and shows the
            exact dB value that will be recorded on click. Helps the
            audiologist land the click precisely.                    */}
        {!readOnly && hover && (
          <g data-testid={`oae-graph-${ear}-hover`} style={{ pointerEvents: 'none' }}>
            <line
              x1={xScale(hover.freqIdx)} x2={xScale(hover.freqIdx)}
              y1={marginT} y2={h - marginB}
              stroke={plotKind === 'noise' ? '#64748b' : oaeColor}
              strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5"
            />
            <circle
              cx={xScale(hover.freqIdx)} cy={yScale(hover.db)}
              r="4"
              fill={plotKind === 'noise' ? '#ffffff' : oaeColor}
              stroke={plotKind === 'noise' ? '#64748b' : oaeColor}
              strokeWidth="1.5"
              opacity="0.85"
            />
            <rect
              x={xScale(hover.freqIdx) + 6}
              y={Math.max(marginT, yScale(hover.db) - 10)}
              width="52" height="14" rx="2"
              fill="#0f172a" opacity="0.85"
            />
            <text
              x={xScale(hover.freqIdx) + 8}
              y={Math.max(marginT, yScale(hover.db) - 10) + 10}
              fontSize="9" fill="#ffffff" fontWeight="600"
            >
              {fmtFreq(hover.freq)}k · {hover.db} dB
            </text>
          </g>
        )}
      </svg>
      <div className="flex items-center gap-3 text-[10px] mt-1 text-slate-600">
        <span className="inline-flex items-center gap-1">
          <span style={{ background: oaeColor, width: 12, height: 3, display: 'inline-block' }} /> OAE Response
        </span>
        <span className="inline-flex items-center gap-1">
          <span style={{ borderTop: '1.5px dashed #64748b', width: 12, display: 'inline-block' }} /> Noise Floor
        </span>
        <span className="inline-flex items-center gap-1">
          <span style={{ background: '#dcfce7', width: 12, height: 8, display: 'inline-block', border: '1px solid #86efac' }} /> Normal band (0..+30)
        </span>
      </div>
    </div>
  );
};

export default OAEGraph;
